import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Collapse from '@mui/material/Collapse';
import { useMemo, useState } from 'react';

import { faqData } from './data';
import {
  Answer,
  Badge,
  Card,
  CardButton,
  CardHeader,
  CardHeaderContent,
  Categories,
  CategoryChip,
  ClearButton,
  Container,
  EmptyState,
  EmptySubtitle,
  EmptyTitle,
  GhostButton,
  Header,
  Highlight,
  InlineLink,
  List,
  MetaActions,
  MetaRow,
  MetaText,
  Question,
  SearchIconSlot,
  SearchInput,
  SearchWrapper,
  Section,
  Subtitle,
  Title,
} from './styles';

const ALL_CATEGORIES = 'Todas';

export function FAQSection() {
  const [search, setSearch] = useState('');
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES);

  const categories = useMemo(() => [ALL_CATEGORIES, ...new Set(faqData.map((item) => item.category))], []);

  const filteredFaqs = useMemo(() => {
    const term = search.toLowerCase();

    return faqData.filter((faq) => {
      const matchesCategory = selectedCategory === ALL_CATEGORIES || faq.category === selectedCategory;

      const matchesSearch
        = !term || faq.question.toLowerCase().includes(term) || faq.answer.toLowerCase().includes(term);

      return matchesCategory && matchesSearch;
    });
  }, [search, selectedCategory]);

  const toggleFaq = (index: number) => {
    setExpandedIndexes((current) =>
      current.includes(index) ? current.filter((i) => i !== index) : [...current, index],
    );
  };

  const expandAll = () => {
    setExpandedIndexes(filteredFaqs.map((_, index) => index));
  };

  const collapseAll = () => {
    setExpandedIndexes([]);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory(ALL_CATEGORIES);
    setExpandedIndexes([]);
  };

  const highlightText = (text: string) => {
    if (!search.trim()) return text;

    const regex = new RegExp(`(${search})`, 'gi');

    return text
      .split(regex)
      .map((part, index) =>
        part.toLowerCase() === search.toLowerCase() ? <Highlight key={index}>{part}</Highlight> : part,
      );
  };

  const renderAnswer = (text: string) => {
    const parts = text.split(/(\(\d{2}\)\s*\d{4}-\d{4}|https?:\/\/[^\s]+)/g);

    return parts.map((part, index) => {
      if (part.match(/\(\d{2}\)\s*\d{4}-\d{4}/)) {
        const phone = part.replace(/\D/g, '');

        return (
          <InlineLink key={index} href={`https://wa.me/55${phone}`} target="_blank" rel="noopener noreferrer">
            {part}
          </InlineLink>
        );
      }

      if (part.startsWith('http')) {
        return (
          <InlineLink key={index} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </InlineLink>
        );
      }

      return part;
    });
  };

  return (
    <Section>
      <Container>
        <Header>
          <Title>Perguntas frequentes</Title>
          <Subtitle>Tudo que você precisa saber</Subtitle>
        </Header>

        <SearchWrapper>
          <SearchIconSlot>
            <SearchIcon fontSize="small" />
          </SearchIconSlot>

          <SearchInput placeholder="Buscar perguntas..." value={search} onChange={(e) => setSearch(e.target.value)} />

          {search 
            && <ClearButton onClick={() => setSearch('')}>
              <CloseIcon fontSize="small" />
            </ClearButton>
          }
        </SearchWrapper>

        <Categories>
          {categories.map((category) => 
            <CategoryChip
              key={category}
              label={category}
              selected={category === selectedCategory}
              onClick={() => setSelectedCategory(category)}
            />
          )}
        </Categories>

        <MetaRow>
          <MetaText>
            {filteredFaqs.length} {filteredFaqs.length === 1 ? 'resultado' : 'resultados'}
            {search 
              && <>
                {' '}
                {`para "`}
                <strong>{search}</strong>
                {`"`}
              </>
            }
          </MetaText>

          <MetaActions>
            <GhostButton onClick={expandAll}>
              <ExpandMoreIcon fontSize="small" />
              Expandir todas
            </GhostButton>

            <GhostButton onClick={collapseAll}>
              <ExpandLessIcon fontSize="small" />
              Fechar todas
            </GhostButton>
          </MetaActions>
        </MetaRow>

        <List>
          {filteredFaqs.length === 0 
            && <EmptyState>
              <InfoOutlinedIcon />
              <EmptyTitle>Nenhuma pergunta encontrada</EmptyTitle>
              <EmptySubtitle>Tente outros termos</EmptySubtitle>
              <GhostButton onClick={clearFilters}>Limpar filtros</GhostButton>
            </EmptyState>
          }

          {filteredFaqs.map((faq, index) => {
            const isOpen = expandedIndexes.includes(index);

            return (
              <Card key={`${faq.question}-${index}`}>
                <CardButton onClick={() => toggleFaq(index)}>
                  <CardHeader>
                    <CardHeaderContent>
                      <Badge>{faq.category}</Badge>
                      <Question>{highlightText(faq.question)}</Question>
                    </CardHeaderContent>

                    {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </CardHeader>

                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Answer>{search ? highlightText(faq.answer) : renderAnswer(faq.answer)}</Answer>
                  </Collapse>
                </CardButton>
              </Card>
            );
          })}
        </List>
      </Container>
    </Section>
  );
}
